import React, { memo } from 'react';
import { Button, Collapse, Spin, Tag } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { ComplianceCheckStatus } from '__generated__/graphql';
import {
  ActionRow,
  ChipRow,
  FailedIcon,
  HeaderLeft,
  HeaderRow,
  PassedIcon,
  RecommendationLabel,
  SpinnerIcon,
  StyledCard,
  ValidationSection,
} from './styles';

const { Panel } = Collapse;

export interface LineItemReasonabilityCheckProps {
  /** Latest REASONABILITY compliance-check status for the line. */
  status?: ComplianceCheckStatus | null;
  /** Current primary HTS on the line (shown in the failed-state recommendation). */
  currentHts?: string;
  /** AI-recommended HTS (from the DeepThought classification result). */
  recommendedHts?: string;
  /** Recommendation confidence (fraction 0-1 or percentage 0-100). */
  confidence?: number;
  /** Validate HTS action, rendered in-card when reasonability is shown. */
  onValidateHts?: () => void;
  validateLoading?: boolean;
  /** Accept the recommended HTS (updates the primary HTS). */
  onAcceptRecommendation?: () => void;
  /** Decline the recommendation and keep the current HTS. */
  onDeclineOverride?: () => void;
  /** True once the HTS recommendation has been accepted/validated. */
  htsValidationPassed?: boolean;
  /** Confidence for the accepted/validated HTS. */
  htsValidationConfidence?: number;
  /** Reasoning for the accepted/validated HTS (from the classification result). */
  htsValidationReasoning?: string;
  /** AI rationale for the reasonability verdict (from GetReasonabilityEvaluation). */
  rationale?: string;
  /** Confidence score for the reasonability evaluation, when available. */
  reasonabilityConfidence?: number;
  /** True while the reasonability details are being fetched. */
  detailsLoading?: boolean;
}

// Fallback shown until the reasoning is available.
const REASONING_PLACEHOLDER = 'Reasoning will appear here.';

const formatConfidence = (confidence?: number): string | undefined => {
  if (confidence == null) return undefined;
  const pct = confidence <= 1 ? confidence * 100 : confidence;
  return `${Math.round(pct)}% confidence`;
};

// Line-item card for the AI reasonability check result. Renders one of three
// states (Passed / Failed / In Progress) and nothing otherwise. Passed hosts
// the Validate HTS action; Failed surfaces the DeepThought HTS recommendation
// with Accept / Decline actions.
export const LineItemReasonabilityCheck: React.FC<
  LineItemReasonabilityCheckProps
> = ({
  status,
  currentHts,
  recommendedHts,
  confidence,
  onValidateHts,
  validateLoading,
  onAcceptRecommendation,
  onDeclineOverride,
  htsValidationPassed,
  htsValidationConfidence,
  htsValidationReasoning,
  rationale,
  reasonabilityConfidence,
  detailsLoading,
}) => {
  const isPassed = status === ComplianceCheckStatus.COMPLETED;
  const isFailed = status === ComplianceCheckStatus.FAILED;
  const isInProgress = status === ComplianceCheckStatus.IN_PROGRESS;
  if (!isPassed && !isFailed && !isInProgress) return null;

  // The recommendation is produced by the Validate HTS (DeepThought) flow and
  // can appear in any state once a result is returned, not just on failure.
  const hasRecommendation = Boolean(recommendedHts);

  const title = isPassed
    ? 'Reasonability Check Passed'
    : isFailed
    ? 'Reasonability Check Failed'
    : 'Checking reasonability...';

  const icon = isPassed ? (
    <PassedIcon />
  ) : isFailed ? (
    <FailedIcon />
  ) : (
    <Spin indicator={<SpinnerIcon spin />} size="small" />
  );

  const confidenceLabel = formatConfidence(confidence);
  const htsConfidenceLabel = formatConfidence(htsValidationConfidence);
  const reasonabilityConfidenceLabel = formatConfidence(
    reasonabilityConfidence,
  );

  return (
    <StyledCard size="small" data-testid={`reasonability-check-${status}`}>
      <HeaderRow>
        <HeaderLeft>
          {icon}
          <span>{title}</span>
        </HeaderLeft>
        {onValidateHts &&
          (isPassed || isInProgress) &&
          !hasRecommendation &&
          !htsValidationPassed && (
            <Button
              size="small"
              onClick={onValidateHts}
              loading={validateLoading}
            >
              Validate HTS
            </Button>
          )}
      </HeaderRow>

      {!isInProgress && (
        <>
          {reasonabilityConfidenceLabel ? (
            <RecommendationLabel>
              {reasonabilityConfidenceLabel}
            </RecommendationLabel>
          ) : null}
          <Collapse ghost>
            <Panel key="reasoning" header="Reasoning">
              {detailsLoading
                ? 'Loading reasoning…'
                : rationale || REASONING_PLACEHOLDER}
            </Panel>
          </Collapse>
        </>
      )}

      {isFailed &&
        validateLoading &&
        !hasRecommendation &&
        !htsValidationPassed && (
          <RecommendationLabel>
            <HeaderLeft>
              <Spin indicator={<SpinnerIcon spin />} size="small" />
              <span>Validating HTS…</span>
            </HeaderLeft>
          </RecommendationLabel>
        )}

      {hasRecommendation && !htsValidationPassed && (
        <>
          <RecommendationLabel>
            HTS validation recommendation
          </RecommendationLabel>
          <ChipRow>
            {currentHts ? <Tag color="red">{currentHts}</Tag> : null}
            <ArrowRightOutlined />
            <Tag color="blue">{recommendedHts}</Tag>
            {confidenceLabel ? <span>{confidenceLabel}</span> : null}
          </ChipRow>
          <ActionRow>
            <Button size="small" onClick={onAcceptRecommendation}>
              Accept recommendation
            </Button>
            <Button size="small" onClick={onDeclineOverride}>
              Decline &amp; Override
            </Button>
          </ActionRow>
        </>
      )}

      {htsValidationPassed && (
        <ValidationSection>
          <HeaderLeft>
            <PassedIcon />
            <span>HTS Validation Passed</span>
          </HeaderLeft>
          <Collapse ghost>
            <Panel key="hts-reasoning" header="Reasoning">
              {htsValidationReasoning || REASONING_PLACEHOLDER}
            </Panel>
          </Collapse>
          {htsConfidenceLabel ? (
            <RecommendationLabel>{htsConfidenceLabel}</RecommendationLabel>
          ) : null}
        </ValidationSection>
      )}
    </StyledCard>
  );
};

export default memo(LineItemReasonabilityCheck);
